import { getOptionLabelString } from "ui/select/select.helpers";

import type { OptionType } from "ui/select/select.types";

/**
 * Marks the `Create '…'` entry appended to the list, and holds the text it creates. A symbol, so no call
 * site's option type can carry it by accident.
 */
const CREATE_ENTRY = Symbol("creatable-select-entry");

export type CreateEntry = OptionType & { [CREATE_ENTRY]: string };

/** Keeps the entry's `value` clear of every real option's, which `isSameOption` compares. */
const CREATE_VALUE_PREFIX = "__create__:";

export const isCreateEntry = (option: unknown): option is CreateEntry =>
  typeof option === "object" && option !== null && CREATE_ENTRY in option;

/**
 * Its label is the bare text, and only the list draws `Create '…'` (`createEntryLabel`): MUI writes a
 * picked option's label into the input, which would otherwise read `Create 'Gamma'` — and offer to
 * create that — while the call site saves the new option.
 */
export const buildCreateEntry = (text: string): CreateEntry => ({
  label: text,
  value: `${CREATE_VALUE_PREFIX}${text}`,
  [CREATE_ENTRY]: text,
});

/** The text the entry creates — what `onCreateOption` receives. */
export const createEntryText = (entry: CreateEntry) => entry[CREATE_ENTRY];

export const createEntryLabel = (entry: CreateEntry) => `Create '${createEntryText(entry)}'`;

/** The entry as the list draws it — the bare-text label swapped for `Create '…'`. */
export const withCreateLabel = (entry: CreateEntry): OptionType => ({
  ...entry,
  label: createEntryLabel(entry),
});

/** Whether an option already carries the name, compared case-insensitively. */
export const namesOption = (option: OptionType, text: string) =>
  getOptionLabelString(option).toLowerCase() === text.toLowerCase();

/** Trimmed text that no option names yet — the only text a create entry is offered for. */
export const isNewName = (options: OptionType[], text: string) =>
  !!text && !options.some((option) => namesOption(option, text));

/** MUI's default match — anywhere in the label, ignoring case and accents — on the trimmed text. */
const normalise = (text: string) =>
  text
    .trim()
    .toLowerCase()
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "");

export const matchesText = (label: string, input: string) =>
  normalise(label).includes(normalise(input));
